import { useEffect, useState } from "react";
import { Drawer, notification, Spin } from "antd";
import { ListChecks, Tags } from "lucide-react";

import { Certame } from "../../../sdk/@types";
import CertameService from "../../../sdk/services/CertameService";
import { FilterSection, chipClassName } from "../filters";

export interface CertameStatsDrawerProps {
  open: boolean;
  certame: Certame.Certame | null;
  onClose: () => void;
}

const numberFormatter = new Intl.NumberFormat("pt-BR");

/**
 * Read-only stats drawer for a certame: total linked questions + a per-tag
 * breakdown. Built from the filter design system (FilterSection card chrome,
 * FilterChip's chipClassName) rather than plain antd styling, unlike Certame's
 * own create/edit Drawer.
 */
export default function CertameStatsDrawer({ open, certame, onClose }: CertameStatsDrawerProps) {
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState<number | null>(null);
  const [tagCounts, setTagCounts] = useState<Certame.TagCount[]>([]);

  useEffect(() => {
    if (!open || !certame?.id) return;

    let cancelled = false;
    setLoading(true);

    Promise.all([
      CertameService.getQuestionCount(certame.id),
      CertameService.getQuestionTagCounts(certame.id),
    ])
      .then(([countRes, tagRes]) => {
        if (cancelled) return;
        setTotal(countRes.total);
        setTagCounts(tagRes ?? []);
      })
      .catch((error) => {
        if (cancelled) return;
        notification.error({
          message: "Erro ao carregar estatísticas",
          description: error instanceof Error ? error.message : "Erro inesperado",
        });
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [open, certame?.id]);

  useEffect(() => {
    if (!open) {
      setTotal(null);
      setTagCounts([]);
    }
  }, [open]);

  return (
    <Drawer
      open={open}
      onClose={onClose}
      placement="right"
      width="min(480px, 96vw)"
      title={certame ? `Estatísticas — ${certame.abbreviation}` : "Estatísticas"}
      destroyOnClose={false}
      styles={{
        header: {
          background: "rgb(var(--c-surface))",
          borderBottomColor: "rgb(var(--c-border))",
        },
        body: {
          background: "rgb(var(--c-canvas))",
        },
      }}
    >
      {loading ? (
        <div className="flex items-center justify-center py-16">
          <Spin />
        </div>
      ) : (
        <div className="flex flex-col gap-4">
          <FilterSection
            id="certame-stats-total"
            icon={<ListChecks aria-hidden="true" />}
            accent="blue"
            title="Questões vinculadas"
            description="Total de questões do banco associadas a este certame."
          >
            <p className="text-3xl font-semibold tabular-nums text-ink">
              {numberFormatter.format(total ?? 0)}
            </p>
          </FilterSection>

          <FilterSection
            id="certame-stats-tags"
            icon={<Tags aria-hidden="true" />}
            accent="orange"
            title="Por assunto"
            description="Questões vinculadas, agrupadas por tag."
          >
            {tagCounts.length === 0 ? (
              <p className="text-[13px] text-ink-muted">
                Nenhuma questão vinculada por tag.
              </p>
            ) : (
              <div className="flex max-h-80 flex-wrap gap-2 overflow-y-auto">
                {tagCounts.map((tc) => (
                  <span key={tc.tag} className={chipClassName({ selected: false })}>
                    <span className="truncate">{tc.tag}</span>
                    <span className="rounded-full bg-select/[0.14] px-2 py-0.5 text-[11px] font-semibold tabular-nums text-select-ink">
                      {numberFormatter.format(tc.count)}
                    </span>
                  </span>
                ))}
              </div>
            )}
          </FilterSection>
        </div>
      )}
    </Drawer>
  );
}
